/* Encrypts the complete Vue Router query object into one opaque value. */
const VERSION = 'v1'
const DEFAULT_KEY = 'KAMSLED-route-key-change-me'

function getKey() {
  return process.env.VUE_APP_URL_ENCRYPTION_KEY || DEFAULT_KEY
}

function utf8Encode(value) {
  return unescape(encodeURIComponent(value))
}

function utf8Decode(value) {
  return decodeURIComponent(escape(value))
}

function base64UrlEncode(binary) {
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function base64UrlDecode(value) {
  let base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4) base64 += '='
  return atob(base64)
}

function randomBinary(length) {
  const bytes = new Uint8Array(length)
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < length; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  let output = ''
  for (let i = 0; i < bytes.length; i++) output += String.fromCharCode(bytes[i])
  return output
}

function toUint32Array(binary, includeLength) {
  const length = binary.length
  const result = new Array(Math.ceil(length / 4))
  for (let i = 0; i < result.length; i++) {
    result[i] = (binary.charCodeAt(i * 4) || 0) |
      ((binary.charCodeAt(i * 4 + 1) || 0) << 8) |
      ((binary.charCodeAt(i * 4 + 2) || 0) << 16) |
      ((binary.charCodeAt(i * 4 + 3) || 0) << 24)
  }
  if (includeLength) result.push(length)
  return result
}

function fromUint32Array(values, includeLength) {
  let length = values.length * 4
  if (includeLength) {
    const storedLength = values[values.length - 1]
    length -= 4
    if (storedLength < length - 3 || storedLength > length) throw new Error('Invalid route token')
    length = storedLength
  }
  let result = ''
  for (let i = 0; i < values.length; i++) {
    result += String.fromCharCode(values[i] & 0xff, (values[i] >>> 8) & 0xff,
      (values[i] >>> 16) & 0xff, (values[i] >>> 24) & 0xff)
  }
  return result.substring(0, length)
}

function normalizeKey(key) {
  const source = utf8Encode(key)
  let normalized = ''
  for (let i = 0; i < 16; i++) normalized += source.charAt(i % source.length)
  return toUint32Array(normalized, false)
}

function xxteaEncrypt(binary, key) {
  if (!binary) return binary
  const values = toUint32Array(binary, true)
  const keys = normalizeKey(key)
  const delta = 0x9E3779B9
  let rounds = Math.floor(6 + 52 / values.length)
  let sum = 0
  let z = values[values.length - 1]
  while (rounds-- > 0) {
    sum = (sum + delta) >>> 0
    const e = (sum >>> 2) & 3
    for (let p = 0; p < values.length; p++) {
      const y = values[(p + 1) % values.length]
      const mx = ((((z >>> 5) ^ (y << 2)) + ((y >>> 3) ^ (z << 4))) ^
        ((sum ^ y) + (keys[(p & 3) ^ e] ^ z))) >>> 0
      z = values[p] = (values[p] + mx) >>> 0
    }
  }
  return fromUint32Array(values, false)
}

function xxteaDecrypt(binary, key) {
  if (!binary) return binary
  const values = toUint32Array(binary, false)
  const keys = normalizeKey(key)
  const delta = 0x9E3779B9
  const rounds = Math.floor(6 + 52 / values.length)
  let sum = (rounds * delta) >>> 0
  let y = values[0]
  while (sum !== 0) {
    const e = (sum >>> 2) & 3
    for (let p = values.length - 1; p >= 0; p--) {
      const z = values[(p + values.length - 1) % values.length]
      const mx = ((((z >>> 5) ^ (y << 2)) + ((y >>> 3) ^ (z << 4))) ^
        ((sum ^ y) + (keys[(p & 3) ^ e] ^ z))) >>> 0
      y = values[p] = (values[p] - mx) >>> 0
    }
    sum = (sum - delta) >>> 0
  }
  return fromUint32Array(values, true)
}

export function encryptRouteQuery(query) {
  if (!query || Object.keys(query).length === 0) return ''
  const nonce = randomBinary(8)
  const plaintext = utf8Encode(JSON.stringify(query))
  const ciphertext = xxteaEncrypt(plaintext, getKey() + nonce)
  return VERSION + '.' + base64UrlEncode(nonce) + '.' + base64UrlEncode(ciphertext)
}

export function decryptRouteQuery(token) {
  if (!token || typeof token !== 'string') return {}
  const parts = token.split('.')
  if (parts.length !== 3 || parts[0] !== VERSION) throw new Error('Unsupported route token')
  const nonce = base64UrlDecode(parts[1])
  const plaintext = xxteaDecrypt(base64UrlDecode(parts[2]), getKey() + nonce)
  const parsed = JSON.parse(utf8Decode(plaintext))
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
}

function parsePlainQuery(queryString) {
  const result = {}
  const search = new URLSearchParams(queryString || '')
  search.forEach((value, key) => {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      result[key] = Array.isArray(result[key]) ? result[key].concat(value) : [result[key], value]
    } else {
      result[key] = value
    }
  })
  return result
}

export function parseEncryptedQuery(queryString) {
  const plain = parsePlainQuery(queryString)
  if (!plain.q || Array.isArray(plain.q)) return plain
  try {
    return decryptRouteQuery(plain.q)
  } catch (error) {
    console.warn('Unable to decrypt route query; treating it as a legacy URL.', error)
    return plain
  }
}

export function stringifyEncryptedQuery(query) {
  const token = encryptRouteQuery(query)
  return token ? '?q=' + encodeURIComponent(token) : ''
}
